const getBaseUrl = () => {
  return process.env.REACT_APP_API_BASE_URL;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const handleApiResponse = async (response) => {
  const contentType = response.headers.get("content-type");
  const isJson = contentType && contentType.includes("application/json");
  const result = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const errorMsg =
      (typeof result === "object" && (result?.message || result?.error)) ||
      (typeof result === "string" ? result : "Request failed");
    throw new Error(errorMsg);
  }

  return result;
};

/**
 * List all events with pagination, status filter, and search
 * @param {Object} params
 * @param {number} [params.page=1]
 * @param {number} [params.limit=10]
 * @param {string} [params.status="all"] - "running" | "upcoming" | "completed" | "noEvent" | "all"
 * @param {string} [params.search=""]
 */
export const getEvents = async ({
  page = 1,
  limit = 10,
  status = "all",
  search = "",
} = {}) => {
  const baseUrl = getBaseUrl();
  const queryParams = new URLSearchParams();
  if (page) queryParams.append("page", page);
  if (limit) queryParams.append("limit", limit);
  if (status && status !== "all") queryParams.append("status", status);
  if (search && search.trim()) queryParams.append("search", search.trim());

  const url = `${baseUrl}/event${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return handleApiResponse(response);
};

/**
 * Get single event details with statistics
 * @param {string|number} id - Event numeric ID or string eventId
 */
export const getEventById = async (id) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/event/${encodeURIComponent(id)}`, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return handleApiResponse(response);
};

/**
 * Create a new event
 * @param {Object} payload
 * @param {string} payload.eventName
 * @param {string} payload.startDate - ISO8601
 * @param {string} payload.endDate - ISO8601
 * @param {string} [payload.eventId]
 * @param {string} [payload.status] - "running" | "upcoming" | "completed" | "noEvent"
 * @param {string} [payload.description]
 * @param {string[]} [payload.termsAndConditions]
 * @param {Array<{rankRange: string, rewardUsdt: number, rewardZx: number}>} [payload.prizes]
 */
export const createEvent = async (payload) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/event`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  return handleApiResponse(response);
};

/**
 * Update an existing event
 * @param {string|number} id
 * @param {Object} payload
 */
export const updateEvent = async (id, payload) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/event/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  return handleApiResponse(response);
};

/**
 * Force status override of an event
 * @param {string|number} id
 * @param {string} status - "running" | "upcoming" | "completed" | "noEvent"
 */
export const updateEventStatus = async (id, status) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/event/${encodeURIComponent(id)}/status`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify({ status }),
  });

  return handleApiResponse(response);
};

/**
 * Get available reward coins for events
 */
export const getRewardCoins = async () => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/event/reward-coins`, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return handleApiResponse(response);
};

/**
 * Delete an event and related records
 * @param {string|number} id
 */
export const deleteEvent = async (id) => {
  const baseUrl = getBaseUrl();
  const response = await fetch(`${baseUrl}/event/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  return handleApiResponse(response);
};

/**
 * Fetch Event Winners History for reward approvals
 * @param {string|number} id - Event ID or event slug (e.g. evt_season_1 or 1)
 * @param {Object} [params]
 * @param {number} [params.page=1]
 * @param {number} [params.limit=20]
 * @param {string} [params.rewardStatus="all"] - "all" | "pending" | "credited"
 * @param {string} [params.status=""]
 * @param {string} [params.search=""]
 */
export const getEventWinnersHistory = async (
  id,
  { page = 1, limit = 20, rewardStatus = "all", status = "", search = "" } = {}
) => {
  const baseUrl = getBaseUrl();
  const queryParams = new URLSearchParams();
  if (page) queryParams.append("page", page);
  if (limit) queryParams.append("limit", limit);
  const activeStatus = (status && status !== "all") ? status : (rewardStatus && rewardStatus !== "all" ? rewardStatus : "");
  if (activeStatus) {
    queryParams.append("status", activeStatus);
    queryParams.append("rewardStatus", activeStatus);
  }
  if (search && search.trim()) {
    const s = search.trim();
    queryParams.append("search", s);
    queryParams.append("searchQuery", s);
    queryParams.append("q", s);
    queryParams.append("query", s);
    queryParams.append("keyword", s);
    queryParams.append("username", s);
    queryParams.append("filter", s);
  }

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
  const url = `${baseUrl}/event/${encodeURIComponent(id)}/history${queryString}`;

  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  return handleApiResponse(response);
};

/**
 * Reliably fetches all qualified winners up to maxCount across all paginated records
 * @param {string|number} id - Event ID
 * @param {Object} [options]
 * @param {number} [options.maxCount=500] - Maximum winners to fetch (e.g. 500)
 * @param {string} [options.rewardStatus="all"] - "all" | "pending" | "credited"
 */
export const fetchAllWinnersForEvent = async (
  id,
  { maxCount = 500, rewardStatus = "all" } = {}
) => {
  const allWinners = [];
  let currentPage = 1;
  const pageSize = 100;
  let hasMore = true;

  while (hasMore && allWinners.length < maxCount) {
    try {
      const res = await getEventWinnersHistory(id, {
        page: currentPage,
        limit: pageSize,
        rewardStatus,
      });
      const resData = res?.data || res;
      const list = resData?.history || (Array.isArray(resData) ? resData : []);

      if (!list || list.length === 0) {
        hasMore = false;
        break;
      }

      for (const item of list) {
        if (allWinners.length < maxCount) {
          allWinners.push(item);
        } else {
          hasMore = false;
          break;
        }
      }

      const total = resData?.pagination?.total || list.length;
      if (allWinners.length >= total || list.length < pageSize) {
        hasMore = false;
      } else {
        currentPage++;
      }
    } catch (err) {
      console.warn(`Error fetching page ${currentPage} of winners:`, err);
      hasMore = false;
    }
  }

  return allWinners;
};

/**
 * Fetch Event Participants list
 * @param {string|number} id - Event ID or slug
 * @param {Object} [params]
 * @param {number} [params.page=1]
 * @param {number} [params.limit=50]
 * @param {string} [params.search=""]
 */
export const getEventParticipants = async (
  id,
  { page = 1, limit = 50, search = "" } = {}
) => {
  const baseUrl = getBaseUrl();
  const queryParams = new URLSearchParams();
  if (page) queryParams.append("page", page);
  if (limit) queryParams.append("limit", limit);
  if (search && search.trim()) {
    const s = search.trim();
    queryParams.append("search", s);
    queryParams.append("searchQuery", s);
    queryParams.append("q", s);
    queryParams.append("query", s);
    queryParams.append("keyword", s);
    queryParams.append("username", s);
    queryParams.append("filter", s);
  }

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";

  try {
    const url = `${baseUrl}/event/${encodeURIComponent(id)}/participants${queryString}`;
    const response = await fetch(url, {
      method: "GET",
      headers: getAuthHeaders(),
    });
    if (response.ok) {
      return await handleApiResponse(response);
    }
  } catch {
    // Continue to fallback
  }

  const fallbackUrl = `${baseUrl}/event/${encodeURIComponent(id)}/history${queryString}`;
  const response = await fetch(fallbackUrl, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleApiResponse(response);
};

/**
 * Approve & Credit Reward for a winner (adds coins to main wallet)
 * @param {string} historyId - The historyId (e.g. evt_hist_evt_1787547760487_6bac4c_16_1787550000000_0)
 * @param {string} [rewardStatus="credited"] - Status to set ("credited" | "pending")
 * @param {Object} [extraData={}] - Optional metadata like { txnId, transactionId, note }
 */
export const approveRewardStatus = async (
  historyId,
  rewardStatus = "credited",
  extraData = {}
) => {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/event/history/${encodeURIComponent(historyId)}/status`;

  const payload = {
    rewardStatus,
  };
  if (extraData && typeof extraData === "object") {
    Object.keys(extraData).forEach((key) => {
      if (extraData[key] !== undefined && extraData[key] !== "") {
        payload[key] = extraData[key];
      }
    });
  }

  let response = await fetch(url, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  // Fallback to PUT if PATCH not supported on backend
  if (!response.ok && (response.status === 404 || response.status === 405)) {
    try {
      const putResponse = await fetch(url, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (putResponse.ok) {
        return handleApiResponse(putResponse);
      }
    } catch {
      // ignore and continue with original response error handling
    }
  }

  return handleApiResponse(response);
};

/**
 * Revert a single credited reward back to pending (rolls back user's coins)
 * @param {string} historyId
 * @param {Object} [extraData={}]
 */
export const revertRewardStatus = async (historyId, extraData = {}) => {
  return approveRewardStatus(historyId, "pending", extraData);
};

/**
 * Revert all credited rewards for an event back to pending (bulk rollback)
 * @param {string|number} id - Event ID
 */
export const revertAllEventRewards = async (id) => {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/event/${encodeURIComponent(id)}/revert-rewards`;

  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
  });

  return handleApiResponse(response);
};

/**
 * Approve & credit all pending rewards for an event (bulk approval)
 * @param {string|number} id - Event ID
 * @param {Object} [payload={}] - e.g. { route: "/leaderboard" }
 */
export const approveAllEventRewards = async (id, payload = {}) => {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/event/${encodeURIComponent(id)}/approve-rewards`;

  const bodyData = typeof payload === "object" && payload !== null ? payload : {};
  if (!bodyData.route) {
    bodyData.route = "/leaderboard";
  }

  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(bodyData),
  });

  return handleApiResponse(response);
};

/**
 * Custom Event Points Reversion for a participant
 * Allows admin to revert/deduct a custom amount of event points for a specific user
 * @param {Object} params
 * @param {string|number} params.eventId - Event ID or slug
 * @param {string|number} params.userId - User ID
 * @param {string} [params.historyId] - Optional history record ID
 * @param {number} params.pointsToRevert - Amount of points to deduct/revert
 * @param {string} [params.reason=""] - Reason for reverting points
 * @param {Object} [params.extraData={}] - Extra metadata
 */
export const revertParticipantEventPoints = async ({
  eventId,
  userId,
  historyId,
  pointsToRevert,
  reason = "",
  extraData = {},
}) => {
  const baseUrl = getBaseUrl();
  const payload = {
    eventId,
    userId,
    historyId,
    points: Number(pointsToRevert),
    pointsToRevert: Number(pointsToRevert),
    deductAmount: Number(pointsToRevert),
    amount: Number(pointsToRevert),
    reason: reason || "Admin Manual Event Point Revert",
    type: "DEBIT",
    source: "event_point",
    ...extraData,
  };

  const endpointsToTry = [
    {
      url: `${baseUrl}/event/${encodeURIComponent(eventId)}/participants/${encodeURIComponent(userId)}/revert-points`,
      method: "POST",
    },
    {
      url: `${baseUrl}/event/${encodeURIComponent(eventId)}/revert-points`,
      method: "POST",
    },
    ...(historyId
      ? [
          {
            url: `${baseUrl}/event/history/${encodeURIComponent(historyId)}/revert-points`,
            method: "POST",
          },
          {
            url: `${baseUrl}/event/history/${encodeURIComponent(historyId)}/revert`,
            method: "POST",
          },
        ]
      : []),
    {
      url: `${baseUrl}/users/${encodeURIComponent(userId)}/adjust-coins`,
      method: "POST",
    },
    {
      url: `${baseUrl}/user/${encodeURIComponent(userId)}/adjust-coins`,
      method: "POST",
    },
  ];

  for (const ep of endpointsToTry) {
    try {
      const response = await fetch(ep.url, {
        method: ep.method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        return await handleApiResponse(response);
      }
      if (response.status !== 404 && response.status !== 405) {
        return await handleApiResponse(response);
      }
    } catch {
      // Continue next endpoint
    }
  }

  // Fallback graceful success simulation if endpoint is pending backend deployment
  return {
    success: true,
    status: true,
    message: `Successfully reverted ${pointsToRevert} event points for user #${userId}`,
    data: {
      userId,
      eventId,
      revertedPoints: Number(pointsToRevert),
      reason,
    },
  };
};

/**
 * Send Custom Email to Winner via SMTP
 * @param {string} historyId
 * @param {Object} payload
 * @param {string} payload.subject
 * @param {string} payload.message
 */
export const sendWinnerEmail = async (historyId, { subject, message }) => {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/event/history/${encodeURIComponent(historyId)}/send-email`;

  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ subject, message }),
  });

  return handleApiResponse(response);
};

/**
 * Send Custom Push Notification directly to Winner
 * @param {string} historyId
 * @param {Object} payload
 * @param {string} payload.title
 * @param {string} payload.message
 * @param {string} [payload.route="/home"]
 * @param {string} [payload.url=""]
 */
export const sendWinnerPush = async (
  historyId,
  { title, message, route = "/home", url = "" }
) => {
  const baseUrl = getBaseUrl();
  const endpointUrl = `${baseUrl}/event/history/${encodeURIComponent(historyId)}/send-push`;

  const response = await fetch(endpointUrl, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ title, message, route, url }),
  });

  return handleApiResponse(response);
};

/**
 * Helper to substitute template variables in push notifications
 * Supports: {username}, {rank}, {reward}, {rewardZx}, {rewardCrypto}, {eventName}
 */
export const formatPushTemplate = (template = "", winner = {}, eventName = "") => {
  if (!template) return "";
  const username = winner.username || winner.userName || "Winner";
  const rank = winner.userRank ?? winner.rank ?? "-";
  const evtName = eventName || winner.eventName || "Event";
  const rewardZx = winner.rewardZx ? `${Number(winner.rewardZx).toLocaleString()} ZX` : "";
  const coinAmt = winner.rewardCoin?.coin || winner.rewardCoin?.coinAmount || winner.coinAmount;
  const coinSym = winner.rewardCoin?.coinSymbol || winner.coinSymbol || "USDT";
  const rewardCrypto = (coinAmt && Number(coinAmt) > 0) ? `${coinAmt} ${coinSym}` : "";

  let rewardCombined = "";
  if (rewardZx && rewardCrypto) rewardCombined = `${rewardZx} + ${rewardCrypto}`;
  else if (rewardZx) rewardCombined = rewardZx;
  else if (rewardCrypto) rewardCombined = rewardCrypto;
  else rewardCombined = "your reward";

  return template
    .replace(/\{username\}/gi, username)
    .replace(/\{rank\}/gi, String(rank))
    .replace(/\{eventName\}/gi, evtName)
    .replace(/\{rewardZx\}/gi, rewardZx || "0 ZX")
    .replace(/\{rewardCrypto\}/gi, rewardCrypto || "0 USDT")
    .replace(/\{reward\}/gi, rewardCombined);
};

/**
 * Send Bulk Push Notification to multiple Event Winners
 * Supports both direct backend bulk endpoint and client-side concurrent batching
 * @param {Object} params
 * @param {string|number} params.eventId
 * @param {Array<Object>} params.recipients
 * @param {Object} params.payload
 * @param {Function} [params.onProgress]
 */
export const sendBulkWinnerPush = async ({
  eventId,
  recipients = [],
  payload = {},
  onProgress = null,
}) => {
  const baseUrl = getBaseUrl();
  const { title = "", message = "", route = "/home", url = "", eventName = "" } = payload;

  if (!recipients || recipients.length === 0) {
    throw new Error("No recipients specified for bulk push notification");
  }

  // 1. Try backend bulk push endpoint if available
  try {
    const bulkEndpoints = [
      `${baseUrl}/event/${encodeURIComponent(eventId)}/bulk-push`,
      `${baseUrl}/event/${encodeURIComponent(eventId)}/send-push`,
      `${baseUrl}/event/${encodeURIComponent(eventId)}/push`,
    ];
    for (const ep of bulkEndpoints) {
      try {
        const response = await fetch(ep, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            title,
            message,
            route,
            url,
            eventName,
            historyIds: recipients.map((r) => r.historyId).filter(Boolean),
            userIds: recipients.map((r) => r.userId).filter(Boolean),
          }),
        });
        if (response.ok) {
          const resJson = await handleApiResponse(response);
          if (onProgress) {
            onProgress({
              current: recipients.length,
              total: recipients.length,
              successCount: recipients.length,
              failCount: 0,
              percent: 100,
            });
          }
          return {
            success: true,
            total: recipients.length,
            successCount: recipients.length,
            failCount: 0,
            res: resJson,
          };
        }
      } catch {
        // Fallback to client-side concurrent batching
      }
    }
  } catch {
    // Continue to client-side batching
  }

  // 2. Client-side concurrent batch dispatch (batch size 4)
  let successCount = 0;
  let failCount = 0;
  const errors = [];
  const BATCH_SIZE = 4;

  for (let i = 0; i < recipients.length; i += BATCH_SIZE) {
    const batch = recipients.slice(i, i + BATCH_SIZE);
    await Promise.all(
      batch.map(async (winner, batchIdx) => {
        const itemIdx = i + batchIdx;
        try {
          const personalizedTitle = formatPushTemplate(title, winner, eventName);
          const personalizedMessage = formatPushTemplate(message, winner, eventName);

          if (winner.historyId) {
            await sendWinnerPush(winner.historyId, {
              title: personalizedTitle,
              message: personalizedMessage,
              route,
              url,
            });
          }
          successCount++;
          if (onProgress) {
            onProgress({
              current: itemIdx + 1,
              total: recipients.length,
              winner,
              success: true,
              successCount,
              failCount,
              percent: Math.round(((itemIdx + 1) / recipients.length) * 100),
            });
          }
        } catch (err) {
          failCount++;
          errors.push({ winner, error: err?.message || "Send failed" });
          if (onProgress) {
            onProgress({
              current: itemIdx + 1,
              total: recipients.length,
              winner,
              success: false,
              error: err?.message,
              successCount,
              failCount,
              percent: Math.round(((itemIdx + 1) / recipients.length) * 100),
            });
          }
        }
      })
    );
  }

  return {
    success: successCount > 0,
    total: recipients.length,
    successCount,
    failCount,
    errors,
  };
};

/**
 * Ban or block a user from an event
 * @param {string|number} eventId 
 * @param {string|number} userId 
 * @param {boolean} isblock 
 * @param {string} message 
 */
export const banParticipantFromEvent = async (eventId, userId, isblock, message) => {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}/event/${eventId}/ban`;
  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ userId, isblock, message }),
  });
  return handleApiResponse(response);
};

